import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  TrendingUp, TrendingDown, Users, Target, Clock, AlertCircle,
  BarChart, PieChart, Activity
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AnalyticsDashboard = ({ getAuthHeader, selectedPeriod }) => {
  const [funnelData, setFunnelData] = useState(null);
  const [heatmapData, setHeatmapData] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, [selectedPeriod]);

  const loadAnalytics = async () => {
    setIsLoading(true);
    try {
      const [funnelRes, heatmapRes, performanceRes] = await Promise.all([
        axios.get(`${API}/admin/stats/funnel?days=${selectedPeriod}`, getAuthHeader()),
        axios.get(`${API}/admin/stats/answer-heatmap?days=${selectedPeriod}`, getAuthHeader()),
        axios.get(`${API}/admin/stats/destination-performance?days=${selectedPeriod}`, getAuthHeader())
      ]);

      setFunnelData(funnelRes.data);
      setHeatmapData(heatmapRes.data);
      setPerformanceData(performanceRes.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Fehler beim Laden der Analytics-Daten');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="analytics-loading">
        <Activity className="spin" size={48} />
        <p>Lade Analytics-Daten...</p>
      </div>
    );
  }

  return (
    <div className="analytics-dashboard">
      {/* Funnel Statistics */}
      <section className="analytics-section">
        <h2><Target size={24} /> Quiz Funnel</h2>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon" style={{backgroundColor: '#E0E7FF'}}>
              <Users size={24} color="#6366F1" />
            </div>
            <div className="stat-content">
              <p className="stat-label">Quiz gestartet</p>
              <p className="stat-value">{funnelData?.total_starts || 0}</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{backgroundColor: '#D1FAE5'}}>
              <TrendingUp size={24} color="#10B981" />
            </div>
            <div className="stat-content">
              <p className="stat-label">Abgeschlossen</p>
              <p className="stat-value">{funnelData?.completed || 0}</p>
              <p className="stat-sublabel">
                {funnelData?.completion_rate?.toFixed(1)}% Completion Rate
              </p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{backgroundColor: '#FEE2E2'}}>
              <TrendingDown size={24} color="#EF4444" />
            </div>
            <div className="stat-content">
              <p className="stat-label">Abgebrochen</p>
              <p className="stat-value">{funnelData?.abandoned || 0}</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{backgroundColor: '#FEF3C7'}}>
              <Clock size={24} color="#F59E0B" />
            </div>
            <div className="stat-content">
              <p className="stat-label">Ø Zeit</p>
              <p className="stat-value">
                {Math.floor((funnelData?.average_time_seconds || 0) / 60)}:{
                  String((funnelData?.average_time_seconds || 0) % 60).padStart(2, '0')
                }
              </p>
              <p className="stat-sublabel">Minuten</p>
            </div>
          </div>
        </div>

        {/* Abandon Points */}
        {funnelData?.abandon_points && Object.keys(funnelData.abandon_points).length > 0 && (
          <div className="abandon-analysis">
            <h3><AlertCircle size={20} /> Abbruch-Punkte</h3>
            <div className="abandon-list">
              {Object.entries(funnelData.abandon_points)
                .sort(([,a], [,b]) => b - a)
                .slice(0, 5)
                .map(([question, count]) => (
                  <div key={question} className="abandon-item">
                    <span className="abandon-question">{question}</span>
                    <div className="abandon-bar-container">
                      <div 
                        className="abandon-bar" 
                        style={{width: `${(count / funnelData.abandoned) * 100}%`}}
                      />
                    </div>
                    <span className="abandon-count">{count} Abbrüche</span>
                  </div>
                ))}
            </div>
          </div>
        )}
      </section>

      {/* Answer Heatmap */}
      <section className="analytics-section">
        <h2><BarChart size={24} /> Antwort-Verteilung</h2>
        <p className="section-description">
          Beliebte und selten gewählte Optionen pro Frage
        </p>
        
        <div className="heatmap-grid">
          {heatmapData?.heatmap && Object.entries(heatmapData.heatmap).map(([questionId, data]) => (
            <div key={questionId} className="heatmap-card">
              <h4>{questionId.replace('q', 'Frage ')}</h4>
              <div className="heatmap-answers">
                {Object.entries(data.answers).slice(0, 5).map(([answer, stats]) => (
                  <div key={answer} className="heatmap-answer">
                    <span className="answer-text" title={answer}>
                      {answer.length > 30 ? answer.substring(0, 30) + '...' : answer}
                    </span>
                    <div className="answer-bar-container">
                      <div 
                        className="answer-bar"
                        style={{
                          width: `${stats.percentage}%`,
                          backgroundColor: stats.percentage > 50 ? '#10B981' : 
                                          stats.percentage > 20 ? '#F59E0B' : '#EF4444'
                        }}
                      />
                      <span className="answer-percentage">{stats.percentage}%</span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="heatmap-total">{data.total_responses} Antworten</p>
            </div>
          ))}
        </div>
      </section>

      {/* Destination Performance */}
      <section className="analytics-section">
        <h2><PieChart size={24} /> Destination Performance</h2>
        <p className="section-description">
          Wie oft jede Destination empfohlen und per E-Mail verschickt wurde
        </p>

        <div className="performance-table-container">
          <table className="performance-table">
            <thead>
              <tr>
                <th>Destination</th>
                <th>Empfehlungen</th>
                <th>Top 3</th>
                <th>E-Mails</th>
                <th>Conversion</th>
              </tr>
            </thead>
            <tbody>
              {performanceData?.destinations?.slice(0, 15).map((dest, index) => (
                <tr key={dest.destination}>
                  <td>
                    <span className="dest-rank">#{index + 1}</span>
                    {dest.destination}
                  </td>
                  <td>{dest.recommended}</td>
                  <td>{dest.top_3}</td>
                  <td>{dest.emails}</td>
                  <td>
                    <span className={`conversion-badge ${
                      dest.email_conversion > 50 ? 'high' : 
                      dest.email_conversion > 20 ? 'medium' : 'low'
                    }`}>
                      {dest.email_conversion}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Low performers warning */}
        {performanceData?.destinations && (
          <div className="performance-insights">
            <h4>💡 Insights</h4>
            <ul>
              <li>
                <strong>{performanceData.destinations.filter(d => d.recommended === 0).length}</strong> Destinationen 
                wurden nie empfohlen
              </li>
              <li>
                <strong>{performanceData.destinations.filter(d => d.email_conversion < 10).length}</strong> Destinationen 
                haben eine Conversion unter 10%
              </li>
              <li>
                Top Destination: <strong>{performanceData.destinations[0]?.destination}</strong> mit{' '}
                <strong>{performanceData.destinations[0]?.recommended}</strong> Empfehlungen
              </li>
            </ul>
          </div>
        )}
      </section>
    </div>
  );
};

export default AnalyticsDashboard;
